import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

const STATUS = { PASS: "#22c55e", FAIL: "#ef4444" };

/** Ch 24.2 — Hydraulic profile (long-section with HGL/EGL). */
export default function HydraulicProfilePanel({ projectId }) {
  const [networks, setNetworks] = useState([]);
  const [networkId, setNetworkId] = useState("");
  const [startNode, setStartNode] = useState("");
  const [profile, setProfile] = useState(null);
  const [active, setActive] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/drainage/networks`);
      setNetworks(data.networks || []);
      if (data.networks?.[0]) {
        setNetworkId(data.networks[0].id);
        setStartNode(data.networks[0].nodes?.[0]?.id || "");
        setActive(data.networks[0]);
      }
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  })(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (networkId) {
      const n = networks.find((x) => x.id === networkId);
      setActive(n);
      if (n?.nodes?.[0]) setStartNode(n.nodes[0].id);
    }
    // eslint-disable-next-line
  }, [networkId]);

  const compute = async () => {
    if (!networkId || !startNode) return;
    try {
      const { data } = await api.get(
        `/projects/${projectId}/drainage/networks/${networkId}/profile`,
        { params: { start_node: startNode } });
      setProfile(data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const view = useMemo(() => {
    if (!profile?.profile?.length) return null;
    const xs = profile.profile.map((p) => p.chainage_m);
    const els = profile.profile.flatMap((p) => [p.ground_level_m,
                                                       p.invert_in_m,
                                                       p.crown_out_m,
                                                       p.hgl_in_m]);
    const xMax = Math.max(...xs) || 100;
    const yMax = Math.max(...els); const yMin = Math.min(...els);
    const sY = 4;     // vertical exaggeration
    return { xMax, yMin, yMax, sY,
              vb: `${-15} ${-(yMax + 1) * sY} ${xMax + 30} ${(yMax - yMin + 2) * sY}` };
  }, [profile]);

  return (
    <div data-testid="hydraulic-profile" style={{ display: "grid",
            gridTemplateColumns: "320px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8 }}>
        <div style={label}>Network</div>
        <select data-testid="hp-network" style={inputStyle}
          value={networkId} onChange={(e) => setNetworkId(e.target.value)}>
          {networks.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
        </select>
        <div style={{ ...label, marginTop: 8 }}>Start node</div>
        <select data-testid="hp-start" style={inputStyle}
          value={startNode} onChange={(e) => setStartNode(e.target.value)}>
          {(active?.nodes || []).map((n) =>
            <option key={n.id} value={n.id}>{n.id} ({n.type})</option>)}
        </select>
        <button data-testid="hp-compute" style={{ ...btn, width: "100%", marginTop: 12 }}
          onClick={compute}>Compute profile</button>
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: 6, borderBottom: "1px solid var(--pf-border)",
                       fontSize: 11, color: "var(--pf-muted)" }}>
          {profile ? `From ${profile.start_node} · ${profile.profile.length} nodes · L = ${profile.total_length_m}m`
                     : "Pick a network + start node and compute"}
        </div>
        {profile && view && (
          <svg viewBox={view.vb} data-testid="hp-canvas"
                style={{ width: "100%", height: "calc(100% - 32px)", background: "#0a1220" }}>
            {/* Ground line */}
            <polyline points={profile.profile.map((p) =>
              `${p.chainage_m},${-p.ground_level_m * view.sY}`).join(" ")}
              fill="none" stroke="#7a4f1b" strokeWidth={1.2} />
            {/* Pipe (invert + crown) */}
            <polyline points={profile.profile.map((p) =>
              `${p.chainage_m},${-p.invert_in_m * view.sY}`).join(" ")}
              fill="none" stroke="#888" strokeWidth={1} strokeDasharray="2,1" />
            <polyline points={profile.profile.map((p) =>
              `${p.chainage_m},${-p.crown_out_m * view.sY}`).join(" ")}
              fill="none" stroke="#888" strokeWidth={1} strokeDasharray="2,1" />
            {/* HGL */}
            <polyline points={profile.profile.map((p) =>
              `${p.chainage_m},${-p.hgl_in_m * view.sY}`).join(" ")}
              fill="none" stroke="#3aa0ff" strokeWidth={1.5} />
            {/* Manholes */}
            {profile.profile.map((p, i) => (
              <g key={i} data-testid={`hp-node-${p.node}`}>
                <line x1={p.chainage_m} y1={-p.ground_level_m * view.sY}
                        x2={p.chainage_m} y2={-p.invert_in_m * view.sY}
                        stroke={STATUS[p.freeboard_status]} strokeWidth={1.2} />
                <text x={p.chainage_m} y={-p.ground_level_m * view.sY - 1.5}
                       fontSize={2} fill="#fff" textAnchor="middle"
                       fontFamily="monospace">{p.node}</text>
                <text x={p.chainage_m + 1} y={-p.invert_in_m * view.sY + 1.2}
                       fontSize={1.6} fill="#aaa" fontFamily="monospace">
                  IL {p.invert_in_m.toFixed(2)}
                </text>
              </g>
            ))}
            {/* Legend */}
            <g transform={`translate(${view.xMax * 0.5}, ${-view.yMax * view.sY - 1})`}>
              <line x1={0}  y1={0} x2={4}  y2={0} stroke="#7a4f1b" strokeWidth={1.2} />
              <text x={5} y={0.6} fontSize={1.5} fill="#fff">ground</text>
              <line x1={15} y1={0} x2={19} y2={0} stroke="#3aa0ff" strokeWidth={1.5} />
              <text x={20} y={0.6} fontSize={1.5} fill="#fff">HGL</text>
            </g>
          </svg>
        )}
      </div>

      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Freeboard table</div>
        {(profile?.profile || []).map((p) => (
          <div key={p.node} data-testid={`hp-row-${p.node}`}
            style={{ padding: 5, marginTop: 4, fontSize: 11, borderRadius: 4,
                      background: "rgba(255,255,255,0.04)",
                      borderLeft: `3px solid ${STATUS[p.freeboard_status]}` }}>
            <b>{p.node}</b> @ ch {p.chainage_m} m
            <div style={{ fontFamily: "monospace", fontSize: 10, color: "#aaa" }}>
              ground {p.ground_level_m.toFixed(2)} · IL {p.invert_in_m.toFixed(2)}
              <br />HGL {p.hgl_in_m.toFixed(2)} · freeboard
              <b style={{ color: STATUS[p.freeboard_status], marginLeft: 4 }}>
                {p.freeboard_m.toFixed(2)} m
              </b> · {p.freeboard_status}
            </div>
          </div>
        ))}
        {!profile && <div style={{ fontSize: 10, color: "#888" }}>—</div>}
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}
